var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useRef, useState } from 'react';
import { InlineText, Text } from '@ui';
import { splitAttachmentName } from '../utils/attachmentFormatting';
import { useAttachmentFieldCommit } from '../hooks/useAttachmentFieldCommit';
/**
 * Inline-editable `Name` cell (E2E-27). Only the basename is `contentEditable` — the extension is
 * a static sibling `<span>`, never inside the editable region, so a blur can never touch it.
 *
 * `InlineText`'s `'text'` variant is an uncontrolled `contentEditable` div (see `InlineEditBase`):
 * React never re-syncs its DOM text from a `children` prop change after the first render, so a
 * failed commit reverts by writing `textContent` back onto the captured blur target directly,
 * not by resetting React state alone.
 */
export function AttachmentNameCell({ item, buildingId }) {
    const { commit } = useAttachmentFieldCommit(buildingId, item.id);
    const [currentName, setCurrentName] = useState(item.name);
    const [error, setError] = useState(null);
    // Refetch-clobber guard (InspectionStatusRenderer.tsx:52-55): a listing refetch replaces `item`
    // without remounting this cell — skip re-syncing while a commit is in flight. Read via a ref,
    // not a state dependency: this effect must only re-run when `item.name` itself changes (a
    // genuine external update), never merely because our own commit below flips `isSaving` back to
    // false — React 17 does not batch state updates made from a Promise callback, so `isSaving`
    // flipping first would otherwise re-run this effect and reset `currentName` from the
    // still-stale `item` prop before the very next line's `setCurrentName(result.row.name)` lands.
    const isSavingRef = useRef(false);
    useEffect(() => {
        if (isSavingRef.current)
            return;
        setCurrentName(item.name);
    }, [item.name]);
    const { basename, extension } = splitAttachmentName(currentName);
    const handleBlur = (event) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        // Capture the DOM node now — `event.currentTarget` is only guaranteed live for the duration
        // of this handler, but the plain node reference it returns keeps working after that.
        const target = event.currentTarget;
        const rawValue = ((_a = target.textContent) !== null && _a !== void 0 ? _a : '').trim();
        // E2E-27: clearing the field reverts to the current name — never saves a blank name. Also
        // covers the no-op case (blurring without changing anything).
        if (!rawValue || rawValue === basename) {
            target.textContent = basename;
            return;
        }
        const nextName = extension ? `${rawValue}.${extension}` : rawValue;
        isSavingRef.current = true;
        setError(null);
        const result = yield commit({ name: nextName });
        isSavingRef.current = false;
        if (result.success) {
            // Server is the source of truth — reflect its confirmed value verbatim, not just the
            // client's optimistic `rawValue` (it may differ, e.g. server-side trimming).
            target.textContent = splitAttachmentName(result.row.name).basename;
            setCurrentName(result.row.name);
            setError(null);
        }
        else {
            target.textContent = basename;
            setError(result.error);
        }
    });
    return (React.createElement("span", { className: "building-attachments-table__name-cell", "data-action-cell": true },
        React.createElement(InlineText, { width: "full", onBlur: (event) => {
                void handleBlur(event);
            } }, basename),
        extension && (React.createElement(Text, { variant: "body", as: "span", className: "building-attachments-table__name-extension" },
            ".",
            extension)),
        error && (React.createElement(Text, { variant: "body", role: "alert", className: "building-attachments-table__field-error building-attachments-table__field-error--stacked" }, error))));
}
